import { Box, Grid } from '@mui/material';
import CustomAccordion from '../../../../components/Accordion/CustomAccordion';
import useResponsibles from './hooks/useResponsibles';
import useContractors from './hooks/useContractors';
import { ResponsiblesAccordionContent } from './components/ResponsiblesAccordionContent';
import { ContractorsAccordionContent } from './components/ContractorsAccordionContent';
import useResponsiblesModule from './hooks/useResponsiblesModule';
import DrawerManager from './components/drawers';
import useAlerts from './hooks/useAlerts';
import BackdropComponent from '../../../../components/Backdrop/BackdropComponent';
import { CarcinogenicModuleFooter } from '../../components/CarcinogenicModuleFooter';
import useSendCarcinogenicReport from '../../hooks/useSendCarcinogenicReport';
import { useNavigate } from 'react-router-dom';
import { TEST_IDS } from './testIds';
import { TAB_INDEX } from '../../pages';
import { enqueueSnackbar } from 'notistack';
import { getAllDuplicateCodes, hasResponsibleWithCode } from './helpers';

interface ResponsiblePartiesAndContractorsTabContentProps {
    showCancelCarcinogenicReportAlert: any;
    IS_REPORT_READY_TO_PRESENT: boolean;
    /** Qué frena el envío, en frases para el usuario (useCarcinogenicTabStates). */
    FALTANTES_PARA_ENVIAR?: string[];
    handleTabChange: any;
    onlyVisual: boolean;
}

const ResponsiblePartiesAndContractorsTabContent = ({
    showCancelCarcinogenicReportAlert,
    IS_REPORT_READY_TO_PRESENT,
    FALTANTES_PARA_ENVIAR = [],
    handleTabChange,
    onlyVisual
}: ResponsiblePartiesAndContractorsTabContentProps) => {
    const {
        selected,
        open,
        translate,
        setOpen,
        handleEditNewContractorIconClick,
        handleEditContractorIconClick,
        handleNewResponsibleIconClick,
        handleEditResponsibleIconClick
    } = useResponsiblesModule();

    const alerts = useAlerts({ translate: translate });

    const contractorsState = useContractors();
    const responsiblesState = useResponsibles();
    const { handleSendCarcinogenicReportClickButtonClick } = useSendCarcinogenicReport({
        isReadyToPresent: IS_REPORT_READY_TO_PRESENT,
        faltantes: FALTANTES_PARA_ENVIAR
    });

    const { contractors, isDeletingContractor, isAddingContractor, isEditingContractor } =
        contractorsState;
    const { responsibles, isDeletingResponsible, isAddingResponsible, isEditingResponsible } =
        responsiblesState;

    const navigate = useNavigate();

    const handleFooterCancelClick = () => {
        if (onlyVisual) {
            handleTabChange(TAB_INDEX.WORKERS_AND_MEDICAL_EXAMS);
            return;
        }
        showCancelCarcinogenicReportAlert({ confirmAction: () => navigate(-1) });
    };

    return (
        <>
            <Box
                component="div"
                justifyContent="center"
                bgcolor={'#ffffff'}
                borderRadius={'0px 0px 6px 6px'}
                p={3}
            >
                {/*
                 * El tema global pinta todo AccordionSummary con #476773 (paleta
                 * vieja), que acá desentona con las tablas verdes. Se corrige
                 * sólo en este paso: colores por las props de CustomAccordion y
                 * el redondeo desde el contenedor, sin tocar el tema.
                 */}
                <Grid
                    container
                    spacing={2}
                    sx={{
                        '& .MuiAccordion-root': {
                            borderRadius: '12px !important',
                            border: '1px solid #E0EBED',
                            overflow: 'hidden',
                            '&::before': { display: 'none' }
                        },
                        '& .MuiAccordionSummary-root': { minHeight: 56 },
                        '& .MuiAccordionSummary-content p': { fontWeight: 600 }
                    }}
                >
                    <Grid item xs={12}>
                        <CustomAccordion
                            testID={TEST_IDS.contractors.accordion}
                            seccion={{
                                id: 1,
                                titulo: translate('accordions.contractors.title'),
                                descripcion: translate('accordions.contractors.subtitle')
                            }}
                            rightButtonText={translate('accordions.contractors.button')}
                            initiallyExpanded={true}
                            rightButton={!onlyVisual}
                            onRightButtonClick={handleEditNewContractorIconClick}
                            // Sin `required`: el envío no exige contratistas (sólo valida
                            // los que se carguen) y el asterisco hacía pensar que sí.
                            summaryBgColor="#F3F8F8"
                            summaryColor="#1E272E"
                            Children={
                                <ContractorsAccordionContent
                                    translate={translate}
                                    handleClickEditIcon={handleEditContractorIconClick}
                                    contractors={contractors}
                                    contractorsState={contractorsState}
                                    alerts={alerts}
                                    onlyVisual={onlyVisual}
                                />
                            }
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <CustomAccordion
                            testID={TEST_IDS.responsibles.accordion}
                            seccion={{
                                id: 2,
                                titulo: translate('accordions.responsibles.title'),
                                descripcion: translate('accordions.responsibles.subtitle')
                            }}
                            rightButtonText={translate('accordions.responsibles.button')}
                            initiallyExpanded={true}
                            rightButton={!onlyVisual}
                            onRightButtonClick={handleNewResponsibleIconClick}
                            required
                            summaryBgColor="#F3F8F8"
                            summaryColor="#1E272E"
                            Children={
                                <ResponsiblesAccordionContent
                                    translate={translate}
                                    responsiblesState={responsiblesState}
                                    responsibles={responsibles}
                                    handleClickEditIcon={handleEditResponsibleIconClick}
                                    alerts={alerts}
                                    onlyVisual={onlyVisual}
                                />
                            }
                        />
                    </Grid>
                </Grid>
                <DrawerManager
                    open={open}
                    setOpen={setOpen}
                    selected={selected}
                    responsiblesState={responsiblesState}
                    contractorsState={contractorsState}
                    onlyVisual={onlyVisual}
                />
                <BackdropComponent
                    loading={
                        isDeletingContractor ||
                        isAddingContractor ||
                        isEditingContractor ||
                        isDeletingResponsible ||
                        isAddingResponsible ||
                        isEditingResponsible
                    }
                />
            </Box>
            <CarcinogenicModuleFooter
                handleCancelClick={handleFooterCancelClick}
                handleSubmitClick={() => {
                    if (onlyVisual) return;

                    let hasErrors = false;
                    if (!hasResponsibleWithCode(responsibles, 'R')) {
                        enqueueSnackbar(
                            translate('enqueues.responsibleValidations.needsFormDataResponsible'),
                            {
                                variant: 'warning'
                            }
                        );
                        hasErrors = true;
                    }

                    if (!hasResponsibleWithCode(responsibles, 'H')) {
                        enqueueSnackbar(
                            translate(
                                'enqueues.responsibleValidations.needsHygieneAndSecurityResponsible'
                            ),
                            {
                                variant: 'warning'
                            }
                        );
                        hasErrors = true;
                    }

                    // La SRT exige los tres roles, no dos: "La tabla Responsable
                    // debe ser ingresada para cada uno de los siguientes cargos:
                    // Responsable del Registro, Responsable de Higiene y
                    // Seguridad, Responsable de Medicina de Trabajo"
                    // (InformacionAdicionalART_V2.1 §3.3.3). Antes Medicina del
                    // Trabajo no se validaba y el envío se rechazaba en la SRT.
                    if (!hasResponsibleWithCode(responsibles, 'M')) {
                        enqueueSnackbar(
                            translate(
                                'enqueues.responsibleValidations.needsOccupationalMedicineResponsible'
                            ),
                            {
                                variant: 'warning'
                            }
                        );
                        hasErrors = true;
                    }

                    const duplicates = getAllDuplicateCodes(responsibles);

                    duplicates.forEach((code) => {
                        enqueueSnackbar(
                            translate(`enqueues.responsibleValidations.onlyOneAllowed.${code}`),
                            {
                                variant: 'warning'
                            }
                        );
                    });

                    if (duplicates.length > 0 || hasErrors) {
                        return;
                    }

                    handleSendCarcinogenicReportClickButtonClick();
                }}
                onlyVisual={onlyVisual}
                disableNextButton={onlyVisual}
                lastPage
            />
        </>
    );
};

export { ResponsiblePartiesAndContractorsTabContent };
